import { Link } from 'react-router';
import {
  CheckCircle,
  Circle,
  ArrowRight,
  RotateCcw,
  GraduationCap,
  FileText,
  ExternalLink,
  ClipboardList,
  Loader2,
  Lock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { courseLink } from '@/lib/course-link';
import type { CorrectiveActionActivity, CaActivityType } from '@/types';

const ACTIVITY_TYPE_LABELS: Record<CaActivityType, string> = {
  course: 'Capacitación',
  read_document: 'Lectura de documento',
  read_material: 'Material',
  practical: 'Actividad práctica',
  quiz: 'Quiz',
};

function activityLabel(a: CorrectiveActionActivity): string {
  if (a.title) return a.title;
  if (a.course) return a.course.title;
  if (a.document) return `${a.document.title} (${a.document.code})`;
  if (a.quiz) return a.quiz.title;
  if (a.material_title) return a.material_title;
  return ACTIVITY_TYPE_LABELS[a.type] ?? a.type;
}

// Fila de una actividad requerida de la acción correctiva (con su CTA según tipo).
// Extraído de corrective-detail.tsx por ENG-LOC-01.
export function ActivityRow({
  activity,
  locked,
  onConfirm,
  confirming,
}: {
  activity: CorrectiveActionActivity;
  actionId: number;
  locked: boolean;
  onConfirm: (activityId: number) => void;
  confirming: boolean;
}) {
  const done = activity.completed_at != null;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-border p-3">
      {done ? (
        <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-label="Completada" />
      ) : locked ? (
        <Lock className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground/60" aria-label="Bloqueada" />
      ) : (
        <Circle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-label="Pendiente" />
      )}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm font-medium',
            locked && !done ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {activityLabel(activity)}
        </p>
        <p className="text-xs text-muted-foreground">
          {ACTIVITY_TYPE_LABELS[activity.type]}
          {!activity.is_required && ' · opcional'}
        </p>
        {!done &&
          (locked ? (
            <p className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="h-4 w-4" /> Completa las actividades anteriores
            </p>
          ) : (
            <ActivityCta activity={activity} onConfirm={onConfirm} confirming={confirming} />
          ))}
      </div>
    </li>
  );
}

function ActivityCta({
  activity,
  onConfirm,
  confirming,
}: {
  activity: CorrectiveActionActivity;
  onConfirm: (activityId: number) => void;
  confirming: boolean;
}) {
  const linkClass =
    'mt-2 inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-background px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10';

  if (activity.type === 'course' && activity.course_id != null) {
    return (
      <Link to={courseLink(activity.course_id)} className={linkClass}>
        <GraduationCap className="h-4 w-4" /> Ir a la capacitación{' '}
        <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }
  if (activity.type === 'quiz' && activity.quiz_id != null) {
    return (
      <Link to={`/quiz/${activity.quiz_id}`} className={linkClass}>
        <RotateCcw className="h-4 w-4" /> Hacer el quiz <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }
  if (activity.type === 'read_document') {
    // I5: deep-link to the specific assigned reading where it can be read +
    // confirmed (which advances the AC's %); the generic /documents list only
    // let the user download the file, never registering the reading.
    const to =
      activity.document_id != null
        ? `/documents/readings?highlight=${activity.document_id}`
        : '/documents/readings';
    return (
      <Link to={to} className={linkClass}>
        <FileText className="h-4 w-4" /> Ir a leer el documento <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }
  if (activity.type === 'read_material') {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {activity.material_url && (
          <a
            href={activity.material_url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            <ExternalLink className="h-4 w-4" /> Abrir material
          </a>
        )}
        <button
          type="button"
          onClick={() => onConfirm(activity.id)}
          disabled={confirming}
          className="mt-0 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {confirming ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          Marcar como leído
        </button>
      </div>
    );
  }
  // practical — signed off by the responsible/instructor; the colaborador only coordinates.
  return (
    <p className="mt-2 inline-flex items-center gap-2 text-xs text-muted-foreground">
      <ClipboardList className="h-4 w-4" />
      Coordina con el responsable para la actividad práctica.
    </p>
  );
}
